import { defaultColors } from '@/client/components/charts/constant';
import { CustomLineData } from '@/client/components/charts/custom-line-series/data';
import { LineChart } from '@/client/components/charts/line-chart';
import { ChartIntervalItem, LineSeriesItem } from '@/client/components/charts/types';
import { BlockLoading } from '@/client/components/fetch-status/block-loading';
import { useAppSettingStore } from '@/client/store/app-store';
import { api } from '@/client/utils/api';
import { frameFillChartData, tickMarkFormatter, TIME_FRAMES } from '@/libs/common/chart';
import { useOptionsPriceHistory } from '@/libs/react-sdk/api/use-options-price-history';
import { MarketDetail, MarketDetailOption, OptionsTimeInterval } from '@/libs/sdk/api';
import { Time, UTCTimestamp } from 'lightweight-charts';
import { useEffect, useMemo, useState } from 'react';

type MarketChartProps = {
  market: MarketDetail;
  className?: string;
};

const intervals: ChartIntervalItem<OptionsTimeInterval>[] = [
  {
    label: '1H',
    value: OptionsTimeInterval['1h'],
    frame: TIME_FRAMES.ONE_HOUR,
  },
  {
    label: '6H',
    value: OptionsTimeInterval['6h'],
    frame: TIME_FRAMES.SIX_HOURS,
  },
  {
    label: '1D',
    value: OptionsTimeInterval['1d'],
    frame: TIME_FRAMES.ONE_DAY,
  },
  {
    label: '1W',
    value: OptionsTimeInterval['1w'],
    frame: TIME_FRAMES.ONE_WEEK,
  },
  {
    label: 'All',
    value: OptionsTimeInterval['all'],
    frame: TIME_FRAMES.ALL,
  },
];
type SeriesItem = LineSeriesItem<Time>;

export const MarketChart = ({ market }: MarketChartProps) => {
  const interval = intervals[4];
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const searchParams = useMemo(() => {
    return {
      baseTokenAddress,
      marketAddress: market.marketAddress,
      timeInterval: interval.value,
    };
  }, [baseTokenAddress, interval.value, market?.marketAddress]);
  const optionsHistory = useOptionsPriceHistory(api, searchParams);
  const isBinary = useMemo(() => market.optionList.length === 2, [market.optionList.length]);
  const [series, setSeries] = useState<SeriesItem[]>([]);
  useEffect(() => {
    if (!market?.optionList || !optionsHistory.data?.snapshots) return setSeries([]);
    const snapshots = optionsHistory.data.snapshots;
    const result: {
      [key: string]: {
        option?: MarketDetailOption;
        data: CustomLineData<Time>[];
      };
    } = {};
    for (let i = 0; i < snapshots.length; i++) {
      const { timestamp, tokenPrices } = snapshots[i];
      for (let j = 0; j < tokenPrices.length; j++) {
        const { tokenAddress, price, decimal } = tokenPrices[j];
        const value = Number(price) / 10 ** (decimal - 2);
        if (!result[tokenAddress]) {
          const option = market.optionList.find(item => item.optionAddress.toLowerCase() === tokenAddress.toLowerCase());
          result[tokenAddress] = {
            option,
            data: [],
          };
        }
        result[tokenAddress].data.push({
          time: Number(timestamp) as Time,
          value,
        });
      }
    }
    const series: SeriesItem[] = Object.keys(result)
      .filter(address => result[address].option !== undefined)
      .map((address, index) => {
        const { option, data } = result[address];
        const label = option?.description || option?.optionName;
        const n = option?.optionName.trim().toLowerCase() ?? '';
        const isYes = isBinary && (n === 'yes' || n === 'option1');
        const isNo = isBinary && (n === 'no' || n === 'option2');
        return {
          label, //  TODO translate
          data: frameFillChartData<CustomLineData<Time>>(data, interval.frame),
          color: isYes ? '#3CBD2C' : isNo ? '#F65570' : defaultColors[index],
          visible: isYes ? true : isNo ? false : index <= 3,
        };
      });
    // default sort
    series.sort((a, b) => {
      // TODO sort
      // if (b.label?.toLowerCase() === 'yes') return 1;
      // if (a.label?.toLowerCase() === 'yes') return -1;
      const aLast = a.data.length ? a.data[a.data.length - 1].value : -Infinity;
      const bLast = b.data.length ? b.data[b.data.length - 1].value : -Infinity;
      return bLast - aLast;
    });
    // visible sort
    series.sort((_, b) => (b.visible ? 1 : 0));
    setSeries(series);
  }, [interval.frame, isBinary, market.optionList, optionsHistory.data?.snapshots]);

  return (
    <LineChart
      showLogo={false}
      series={series}
      options={{
        timeScale: {
          tickMarkFormatter: (time: UTCTimestamp) => tickMarkFormatter(time, interval.frame),
        },
      }}
    >
      {optionsHistory.isLoading && <BlockLoading />}
    </LineChart>
  );
};
